import React, { useState, useEffect } from 'react';
import { FaCamera } from 'react-icons/fa';
import { PhotoGrid, PhotoLightbox, PhotoUpload } from '../PhotoGallery';
import { axiosInstance as axios } from '../../context/AuthContext';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

function PhotosTab({ photos, eventId, familyId, onPhotosUpdated, canUpload = true, canDelete = false }) {
  const [uploading, setUploading] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [albums, setAlbums] = useState([]);

  // Load albums for this family so the lightbox Edit Details panel can
  // show the "Album" dropdown — enabling moves between albums from inside
  // an event's Photos tab too.
  useEffect(() => {
    if (!familyId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/families/${familyId}/albums`, { withCredentials: true });
        if (!cancelled) setAlbums(res.data || []);
      } catch (err) {
        console.error('Could not load albums for lightbox:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [familyId]);

  const handleFileUpload = async (file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('event_id', eventId);
      formData.append('family_id', familyId);
      
      await axios.post(
        `${API}/photos/upload`,
        formData,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      
      onPhotosUpdated();
    } catch (error) {
      console.error('Error uploading photo:', error);
      const message = error.response?.data?.detail || 'Failed to upload photo';
      alert(message);
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;
    
    try {
      await axios.delete(`${API}/photos/${photoId}`, { withCredentials: true });
      onPhotosUpdated();
      if (lightboxOpen && photos.length <= 1) {
        setLightboxOpen(false);
      }
    } catch (error) {
      console.error('Error deleting photo:', error);
      const msg = error?.response?.data?.detail || error?.message || 'Failed to delete photo';
      alert(msg);
    }
  };

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  return (
    <div data-testid="photos-tab">
      {canUpload && (
        <PhotoUpload 
          onUpload={handleFileUpload} 
          uploading={uploading}
          maxSize={10}
        />
      )}

      {photos.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <FaCamera className="text-gray-300 text-6xl mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-800 mb-2">No photos yet</h3>
          <p className="text-gray-600">Start uploading memories from your event!</p>
        </div>
      ) : (
        <PhotoGrid 
          photos={photos}
          onPhotoClick={openLightbox}
          onDelete={canDelete ? handleDeletePhoto : null}
          canDelete={canDelete}
        />
      )}

      {lightboxOpen && (
        <PhotoLightbox
          photos={photos}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
          onDelete={canDelete ? handleDeletePhoto : null}
          canDelete={canDelete}
          albums={albums}
          onUpdate={onPhotosUpdated}
        />
      )}
    </div>
  );
}

export default PhotosTab;
